import { Flex, Spinner } from "@chakra-ui/react";
import { MdDownload, MdRefresh } from "react-icons/md";
import { ApolloError } from "@apollo/client";
import TooltipIconButton, {
  TooltipIconButtonProps,
} from "components/Shared/TooltipIconButton";
import asyncVoidHandler from "utils/asyncVoidHandler";
import UserError from "utils/UserError";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

function TableControlIconButton(props: TooltipIconButtonProps) {
  return <TooltipIconButton variant="ghost" color="gray.600" {...props} />;
}

export type Props = {
  isExporting?: boolean;
  isLoading?: boolean;
  onExport?: () => Promise<unknown>;
  onRefresh?: () => Promise<unknown>;
};

export default function TableControls({
  isExporting = false,
  isLoading = false,
  onExport,
  onRefresh,
}: Props) {
  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const makeHandleButtonClick =
    (
      handler: () => Promise<unknown>,
      {
        successMessage,
        errorMessage,
      }: { successMessage: string; errorMessage: string },
    ) =>
    async () => {
      try {
        await handler();
        showSuccessToast(successMessage);
      } catch (e) {
        showErrorToast(e, { title: errorMessage, description: null });
        if (!(e instanceof ApolloError || e instanceof UserError)) {
          throw e;
        }
      }
    };

  return (
    <Flex alignItems="center">
      {isLoading && <Spinner color="gray" />}

      {onExport && (
        <TableControlIconButton
          icon={<MdDownload />}
          label="Export Table Data"
          isDisabled={isLoading}
          isLoading={isExporting}
          onClick={asyncVoidHandler(
            makeHandleButtonClick(onExport, {
              successMessage: "Table data export completed successfully",
              errorMessage: "An error occurred exporting table data",
            }),
          )}
        />
      )}

      {onRefresh && (
        <TableControlIconButton
          icon={<MdRefresh />}
          label="Refresh Table Data"
          isDisabled={isLoading}
          onClick={asyncVoidHandler(
            makeHandleButtonClick(onRefresh, {
              successMessage: "Table data refreshed successfully",
              errorMessage: "An error occurred refreshing table data",
            }),
          )}
        />
      )}
    </Flex>
  );
}
